import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Users, Award, TrendingUp, BookOpen, Building2, Clock, Target, Briefcase, Globe } from 'lucide-react'
import { useState, useEffect } from 'react'

const StatsSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  const [counters, setCounters] = useState({
    students: 0,
    placement: 0,
    companies: 0,
    courses: 0,
    experience: 0,
    satisfaction: 0
  })

  const stats = [
    {
      icon: <Users className="w-7 h-7" />,
      end: 500,
      suffix: '+',
      label: 'Students Trained',
      description: 'Across India and abroad',
      gradient: 'from-blue-500 to-cyan-500',
      bgGradient: 'from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30'
    },
    {
      icon: <TrendingUp className="w-7 h-7" />,
      end: 95,
      suffix: '%',
      label: 'Placement Rate',
      description: 'Within 6 months',
      gradient: 'from-green-500 to-emerald-500',
      bgGradient: 'from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30'
    },
    {
      icon: <Building2 className="w-7 h-7" />,
      end: 150,
      suffix: '+',
      label: 'Hiring Partners',
      description: 'Top MNCs & startups',
      gradient: 'from-purple-500 to-pink-500',
      bgGradient: 'from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30'
    },
    {
      icon: <Award className="w-7 h-7" />,
      end: 98,
      suffix: '%',
      label: 'Success Rate',
      description: 'Student satisfaction',
      gradient: 'from-orange-500 to-red-500',
      bgGradient: 'from-orange-50 to-red-50 dark:from-orange-950/30 dark:to-red-950/30'
    }
  ]

  useEffect(() => {
    if (inView) {
      const duration = 2000
      const steps = 60
      const stepDuration = duration / steps

      const intervals = stats.map((stat, index) => {
        return setInterval(() => {
          setCounters(prev => {
            const current = prev[Object.keys(prev)[index]]
            const increment = stat.end / steps
            const next = Math.min(current + increment, stat.end)
            
            return {
              ...prev,
              [Object.keys(prev)[index]]: next
            }
          })
        }, stepDuration)
      })

      return () => intervals.forEach(clearInterval)
    }
  }, [inView])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  }

  return (
    <section className="section-padding bg-gradient-to-br from-gray-50 via-white to-primary-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 pointer-events-none opacity-40 dark:opacity-20">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-400/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent-400/20 rounded-full blur-3xl" />
      </div>
      <div className="container-custom relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-1.5 bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400 text-sm font-semibold rounded-full mb-4"
          >
            Our Impact
          </motion.span>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-gray-900 dark:text-white mb-5 tracking-tight">
            Achievements That
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-accent-500">Speak for Themselves</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Transforming careers and building success stories across the globe
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -8, scale: 1.02 }}
              className="relative group"
            >
              <div className={`relative bg-gradient-to-br ${stat.bgGradient} rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden`}>
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${stat.gradient} opacity-10 rounded-full blur-2xl group-hover:opacity-20 transition-opacity" />
                
                <div className="relative z-10">
                  <div className={`inline-flex p-3 bg-gradient-to-br ${stat.gradient} rounded-xl mb-4 shadow-lg`}>
                    <span className="text-white">{stat.icon}</span>
                  </div>
                  
                  <div className="space-y-1 mb-3">
                    <div className={`text-5xl font-extrabold bg-gradient-to-br ${stat.gradient} bg-clip-text text-transparent`}>
                      {Math.round(counters[Object.keys(counters)[index]])}{stat.suffix}
                    </div>
                    <div className="text-base font-bold text-gray-900 dark:text-white">
                      {stat.label}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {stat.description}
                    </div>
                  </div>

                  <div className="relative w-full bg-gray-200 dark:bg-gray-700/50 rounded-full h-1.5 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: '100%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.5, delay: index * 0.15, ease: 'easeOut' }}
                      className={`h-full bg-gradient-to-r ${stat.gradient} rounded-full`}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-accent-600 rounded-3xl p-8 lg:p-12 overflow-hidden shadow-2xl"
        >
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30" />
          
          <div className="relative grid lg:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="text-center lg:text-left group"
            >
              <div className="inline-flex p-3 bg-white/10 backdrop-blur-sm rounded-xl mb-4">
                <Target className="w-8 h-8 text-white" />
              </div>
              <div className="text-2xl lg:text-3xl font-extrabold text-white mb-3">Industry-Ready</div>
              <p className="text-primary-100 leading-relaxed">
                Curriculum co-created with 50+ hiring managers from top tech companies
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="text-center group"
            >
              <div className="inline-flex p-3 bg-white/10 backdrop-blur-sm rounded-xl mb-4">
                <Briefcase className="w-8 h-8 text-white" />
              </div>
              <div className="text-2xl lg:text-3xl font-extrabold text-white mb-3">Lifetime Support</div>
              <p className="text-primary-100 leading-relaxed">
                Career guidance, interview prep, and job assistance even after completion
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="text-center lg:text-right group"
            >
              <div className="inline-flex p-3 bg-white/10 backdrop-blur-sm rounded-xl mb-4">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <div className="text-2xl lg:text-3xl font-extrabold text-white mb-3">Global Recognition</div>
              <p className="text-primary-100 leading-relaxed">
                Industry certifications recognized and valued by employers worldwide
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default StatsSection
